import { IconPen, IconPlusRounded, IconTrash } from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllQuestUnits,
  getAllQuestMissions,
} from "redux/actions/quest";
import styled from "styled-components";
import {
  UnitFormModal,
  UnitConfirmModal,
  MissionFormModal,
  MissionConfirmModal,
  MissionDetailPanel,
} from "./components";
import { InputSelect } from "components/Form/InputSelect";
import { IconAwards } from "@aksara-ui/icons";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 20px;
`;

const QuestsContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { isLoading } = useSelector((state: any) => state.globalReducer);
  const { questUnits = [], questMissions = [] } = useSelector(
    (state: any) => state.questReducer ?? {}
  );

  const unitsList = Array.isArray(questUnits) ? questUnits : [];
  const missionsList = Array.isArray(questMissions) ? questMissions : [];

  // ── Unit modal state ──────────────────────────────────────────────────────
  const [isUnitFormOpen, setIsUnitFormOpen] = useState(false);
  const [isUnitConfirmOpen, setIsUnitConfirmOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState("");

  // ── Mission modal state ───────────────────────────────────────────────────
  const [isMissionFormOpen, setIsMissionFormOpen] = useState(false);
  const [isMissionConfirmOpen, setIsMissionConfirmOpen] = useState(false);
  const [isMissionDetailOpen, setIsMissionDetailOpen] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState("");
  const [selectedMission, setSelectedMission] = useState<any>(null);

  // ── Mission filter ─────────────────────────────────────────────────────────
  const [filterUnitId, setFilterUnitId] = useState<string>("");

  const unitOptions = [
    { label: "Semua Unit", value: "" },
    ...unitsList.map((u: any) => ({ label: u.name, value: String(u.id) })),
  ];

  // ── Initial data load ──────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(getAllQuestUnits() as any);
    dispatch(getAllQuestMissions() as any);
  }, [dispatch]);

  // ── Filter handler ─────────────────────────────────────────────────────────
  const handleFilterUnit = (unitId: string) => {
    setFilterUnitId(unitId);
    dispatch(
      getAllQuestMissions(unitId ? { master_unit_id: unitId } : undefined) as any
    );
  };

  // ── Unit handlers ──────────────────────────────────────────────────────────
  const openUnitForm = (unitId: string = "") => {
    setSelectedUnitId(unitId);
    setIsUnitFormOpen(true);
  };
  const closeUnitForm = () => {
    setIsUnitFormOpen(false);
    setSelectedUnitId("");
  };
  const openUnitConfirm = (unitId: string) => {
    setSelectedUnitId(unitId);
    setIsUnitConfirmOpen(true);
  };
  const closeUnitConfirm = () => {
    setIsUnitConfirmOpen(false);
    setSelectedUnitId("");
  };

  // ── Mission handlers ───────────────────────────────────────────────────────
  const openMissionForm = (missionId: string = "") => {
    setSelectedMissionId(missionId);
    setIsMissionFormOpen(true);
  };
  const closeMissionForm = () => {
    setIsMissionFormOpen(false);
    setSelectedMissionId("");
  };
  const openMissionConfirm = (missionId: string) => {
    setSelectedMissionId(missionId);
    setIsMissionConfirmOpen(true);
  };
  const closeMissionConfirm = () => {
    setIsMissionConfirmOpen(false);
    setSelectedMissionId("");
  };
  const openMissionDetail = (mission: any) => {
    setSelectedMission(mission);
    setIsMissionDetailOpen(true);
  };
  const closeMissionDetail = () => {
    setIsMissionDetailOpen(false);
    setSelectedMission(null);
  };

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 20 : 50}>
      <HeaderWrapper display={"flex"} flexDirection={"column"} style={{ gap: 20 }}>
        {/* Page Title */}
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <IconAwards size={28} fill={theme.colors.blue06} />
          <Text scale={[500, 600]} fontWeight={700}>
            Quest Management
          </Text>
        </Box>
      </HeaderWrapper>

      <Tabs>
        <TabList
          backgroundColor={"white"}
          borderBottom={`1px solid ${theme.colors.grey03}`}
          style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0 }}
        >
          <Tab>Master Unit ({questUnits.length})</Tab>
          <Tab>Quests / Missions ({questMissions.length})</Tab>
        </TabList>

        <TabPanels>
          {/* ─────────────────────────────────────────────────────────────────
              TAB 1: UNITS
          ───────────────────────────────────────────────────────────────── */}
          <TabPanel>
            <Box paddingY={20}>
              {/* Header actions */}
              <Box
                display="flex"
                justifyContent="flex-end"
                marginBottom={16}
              >
                <Button
                  variant="primary"
                  borderRadius={10}
                  style={{ fontWeight: 600 }}
                  onClick={() => openUnitForm()}
                >
                  <IconPlusRounded fill="white" />
                  &nbsp;Tambah Unit
                </Button>
              </Box>

              <Card>
                <Box overflowX="auto">
                  <Table>
                    <TableHead>
                      <TableHeadRow>
                        <TableHeadCell style={{ width: 60 }}>No</TableHeadCell>
                        <TableHeadCell>Nama Unit</TableHeadCell>
                        <TableHeadCell>Slug</TableHeadCell>
                        <TableHeadCell>Deskripsi</TableHeadCell>
                        <TableHeadCell>Status</TableHeadCell>
                        <TableHeadCell textAlign="center">Aksi</TableHeadCell>
                      </TableHeadRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={6} />
                      ) : unitsList.length === 0 ? (
                        <TableBodyRow>
                          <TableBodyCell colSpan={6} textAlign="center">
                            <Box paddingY={30}>
                              <Text scale={300} color={theme.colors.grey06}>
                                Belum ada unit. Klik "Tambah Unit" untuk memulai.
                              </Text>
                            </Box>
                          </TableBodyCell>
                        </TableBodyRow>
                      ) : (
                        unitsList.map((unit: any, index: number) => (
                          <TableBodyRow key={unit.id}>
                            <TableBodyCell>{index + 1}</TableBodyCell>
                            <TableBodyCell>
                              <Text fontWeight={600}>{unit.name}</Text>
                            </TableBodyCell>
                            <TableBodyCell>
                              <Badge size="sm" variant="default">
                                {unit.slug}
                              </Badge>
                            </TableBodyCell>
                            <TableBodyCell>
                              <Text scale={300} color={theme.colors.grey07}>
                                {unit.description ?? "-"}
                              </Text>
                            </TableBodyCell>
                            <TableBodyCell>
                              <Badge
                                size="sm"
                                variant={unit.is_active ? "success" : "default"}
                              >
                                {unit.is_active ? "Aktif" : "Nonaktif"}
                              </Badge>
                            </TableBodyCell>
                            <TableBodyCell textAlign="center">
                              <Stack
                                display="flex"
                                flexDirection="row"
                                spacing="xs"
                                justifyContent="center"
                              >
                                <IconButton
                                  onClick={() => openUnitForm(String(unit.id))}
                                >
                                  <IconPen fill={theme.colors.blue06} />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    openUnitConfirm(String(unit.id))
                                  }
                                >
                                  <IconTrash fill={theme.colors.red05} />
                                </IconButton>
                              </Stack>
                            </TableBodyCell>
                          </TableBodyRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            </Box>
          </TabPanel>

          {/* ─────────────────────────────────────────────────────────────────
              TAB 2: MISSIONS / QUESTS
          ───────────────────────────────────────────────────────────────── */}
          <TabPanel>
            <Box paddingY={20}>
              {/* Header: Filter + Add Button */}
              <Box
                display="flex"
                flexDirection={isTablet ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isTablet ? "flex-start" : "center"}
                marginBottom={16}
                style={{ gap: 12 }}
              >
                {/* Filter by unit */}
                <Box style={{ minWidth: 220 }}>
                  <InputSelect
                    label=""
                    placeholder="Filter by Unit"
                    items={unitOptions}
                    selectedItem={
                      unitOptions.find((o: any) => o.value === filterUnitId) ?? unitOptions[0]
                    }
                    itemToString={(item: any) => item?.label ?? ""}
                    handleSelectedItemChange={({ selectedItem }: any) =>
                      handleFilterUnit(selectedItem?.value ?? "")
                    }
                  />
                </Box>

                <Button
                  variant="primary"
                  borderRadius={10}
                  style={{ fontWeight: 600 }}
                  onClick={() => openMissionForm()}
                >
                  <IconPlusRounded fill="white" />
                  &nbsp;Tambah Quest
                </Button>
              </Box>

              <Card>
                <Box overflowX="auto">
                  <Table>
                    <TableHead>
                      <TableHeadRow>
                        <TableHeadCell style={{ width: 60 }}>No</TableHeadCell>
                        <TableHeadCell>Nama Quest</TableHeadCell>
                        <TableHeadCell>Unit</TableHeadCell>
                        <TableHeadCell>Reward Pts</TableHeadCell>
                        <TableHeadCell>Badge</TableHeadCell>
                        <TableHeadCell>Status</TableHeadCell>
                        <TableHeadCell textAlign="center">Aksi</TableHeadCell>
                      </TableHeadRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={7} />
                      ) : missionsList.length === 0 ? (
                        <TableBodyRow>
                          <TableBodyCell colSpan={7} textAlign="center">
                            <Box paddingY={30}>
                              <Text scale={300} color={theme.colors.grey06}>
                                Belum ada quest. Klik "Tambah Quest" untuk memulai.
                              </Text>
                            </Box>
                          </TableBodyCell>
                        </TableBodyRow>
                      ) : (
                        missionsList.map((mission: any, index: number) => (
                          <TableBodyRow
                            key={mission.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => openMissionDetail(mission)}
                          >
                            <TableBodyCell onClick={(e: any) => e.stopPropagation()}>
                              {index + 1}
                            </TableBodyCell>
                            <TableBodyCell>
                              <Text fontWeight={600}>{mission.name}</Text>
                              <Text scale={200} color={theme.colors.grey06}>
                                {mission.description}
                              </Text>
                            </TableBodyCell>
                            <TableBodyCell>
                              <Badge size="sm" variant="default">
                                {mission.unit?.name ?? mission.master_unit_id ?? "-"}
                              </Badge>
                            </TableBodyCell>
                            <TableBodyCell whiteSpace={"nowrap"}>
                              <Badge size="sm" variant="info">
                                {(mission.reward_points ?? 0).toLocaleString()} Pts
                              </Badge>
                            </TableBodyCell>
                            <TableBodyCell>
                              <Text scale={300}>
                                {mission.badge_name ?? "-"}
                              </Text>
                              {mission.tier_name && (
                                <Badge size="sm" variant="warning">
                                  {mission.tier_name}
                                </Badge>
                              )}
                            </TableBodyCell>
                            <TableBodyCell>
                              <Badge
                                size="sm"
                                variant={mission.is_active ? "success" : "default"}
                              >
                                {mission.is_active ? "Aktif" : "Nonaktif"}
                              </Badge>
                            </TableBodyCell>
                            <TableBodyCell
                              textAlign="center"
                              onClick={(e: any) => e.stopPropagation()}
                            >
                              <Stack
                                display="flex"
                                flexDirection="row"
                                spacing="xs"
                                justifyContent="center"
                              >
                                <IconButton
                                  onClick={() =>
                                    openMissionForm(String(mission.id))
                                  }
                                >
                                  <IconPen fill={theme.colors.blue06} />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    openMissionConfirm(String(mission.id))
                                  }
                                >
                                  <IconTrash fill={theme.colors.red05} />
                                </IconButton>
                              </Stack>
                            </TableBodyCell>
                          </TableBodyRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* ─── Unit Modals ──────────────────────────────────────────────────── */}
      <UnitFormModal
        isOpen={isUnitFormOpen}
        onClose={closeUnitForm}
        unitId={selectedUnitId}
      />
      <UnitConfirmModal
        isOpen={isUnitConfirmOpen}
        onClose={closeUnitConfirm}
        unitId={selectedUnitId}
      />

      {/* ─── Mission Modals ───────────────────────────────────────────────── */}
      <MissionFormModal
        isOpen={isMissionFormOpen}
        onClose={closeMissionForm}
        missionId={selectedMissionId}
      />
      <MissionConfirmModal
        isOpen={isMissionConfirmOpen}
        onClose={closeMissionConfirm}
        missionId={selectedMissionId}
      />
      <MissionDetailPanel
        isOpen={isMissionDetailOpen}
        onClose={closeMissionDetail}
        mission={selectedMission}
      />
    </Box>
  );
};

export default QuestsContent;
